// src/components/PdfPreviewStep.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';

export default function PdfPreviewStep({
  cert, editedSample, editedContact, results, charts, richText,
  selectedDoctor, pdfSpacing, setPdfSpacing, lang, onBlobReady,
  editedLabelImage, editedLabelUrl,
}) {
  const de = lang === 'de';
  const [blobUrl,     setBlobUrl]     = useState(null);
  const [rendering,   setRendering]   = useState(false);
  const [renderError, setRenderError] = useState('');
  const debounceTimer = useRef(null);

  const renderPreview = useCallback(async () => {
    setRendering(true);
    setRenderError('');
    try {
      const { generatePDF } = await import('../utils/pdfGenerator');
      const { getUserLogo }  = await import('../pages/Settings');
      const pdfData = {
        certId:             cert.certId,
        includeQR:          cert.includeQR !== false,
        includeLogo:        cert.includeLogo !== false,
        logoDataUrl:        getUserLogo(),
        productLabelUrl:    editedLabelImage || editedLabelUrl || cert.productLabelUrl || '',
        doctorName:         selectedDoctor?.name         || '',
        doctorTitle:        selectedDoctor?.title        || '',
        doctorSignatureUrl: selectedDoctor?.signatureUrl || '',
        results,
        charts,
        richText,
        contact:  editedContact || cert.contact,
        sample:   editedSample  || cert.sample,
        analysis: cert.analysis,
        product:  cert.product,
        specs:    cert.specs,
        lab:      cert.lab,
        _spacing:    pdfSpacing,
        _returnBlob: true,
      };
      const blob = await generatePDF(pdfData, cert.lang || lang);
      if (blob instanceof Blob) {
        const newUrl = URL.createObjectURL(blob);
        setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return newUrl; });
        onBlobReady && onBlobReady(newUrl);
      }
    } catch (e) {
      console.error('Preview error:', e);
      setRenderError(e?.message || 'Preview failed');
    } finally {
      setRendering(false);
    }
  }, [cert, editedSample, editedContact, results, charts, richText, selectedDoctor, pdfSpacing, lang]);

  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(renderPreview, 700);
    return () => clearTimeout(debounceTimer.current);
  }, [renderPreview]);

  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, []);

  const spacingControls = [
    ['afterSpecs',   de ? 'Nach Spez.-Tabelle'  : 'After Specs table'],
    ['afterResults', de ? 'Nach Ergebnissen'    : 'After Results bar'],
    ['afterContact', de ? 'Nach Kontaktinfo'    : 'After Contact info'],
    ['afterCharts',  de ? 'Nach Diagrammen'     : 'After Charts'],
    ['afterMethods', de ? 'Nach Methoden'       : 'After Methods'],
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

      {/* Main row: controls + iframe */}
      <div style={{ display: 'flex', gap: 14, height: 460 }}>

        {/* Left panel — sliders */}
        <div style={{ width: 195, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 7, overflowY: 'auto', paddingRight: 2 }}>
          <div style={cs.panelTitle}>{de ? '📐 Abstände (mm)' : '📐 Spacing (mm)'}</div>
          <p style={cs.hint}>{de ? 'Schieberegler bewegen — PDF aktualisiert sich.' : 'Move sliders — PDF updates live.'}</p>

          {spacingControls.map(([key, lbl]) => (
            <div key={key} style={cs.sliderBlock}>
              <div style={cs.sliderLabel}>{lbl}</div>
              <div style={cs.sliderRow}>
                <input
                  type="range" min={-30} max={60} step={1}
                  value={pdfSpacing[key] || 0}
                  onChange={e => setPdfSpacing(p => ({ ...p, [key]: parseInt(e.target.value) }))}
                  style={cs.slider}
                />
                <span style={cs.sliderVal}>
                  {(pdfSpacing[key] || 0) > 0 ? '+' : ''}{pdfSpacing[key] || 0}
                </span>
              </div>
              <button style={cs.resetBtn} onClick={() => setPdfSpacing(p => ({ ...p, [key]: 0 }))}>
                reset
              </button>
            </div>
          ))}

          <button
            style={cs.resetAllBtn}
            onClick={() => setPdfSpacing({ afterSpecs:0, afterResults:0, afterContact:0, afterCharts:0, afterMethods:0 })}>
            {de ? '↺ Alle zurücksetzen' : '↺ Reset all'}
          </button>
        </div>

        {/* Right panel — live PDF iframe */}
        <div style={{ flex: 1, position: 'relative', background: '#525659', borderRadius: 10, overflow: 'hidden' }}>
          {rendering && (
            <div style={cs.overlay}>
              <div style={cs.spinner} />
              <span style={{ color: '#fff', fontSize: 12, marginTop: 8 }}>
                {de ? 'Wird gerendert...' : 'Rendering...'}
              </span>
            </div>
          )}
          {renderError && !rendering && (
            <div style={cs.errorBox}>
              ⚠️ {renderError}
              <button style={cs.retryBtn} onClick={renderPreview}>
                {de ? 'Erneut' : 'Retry'}
              </button>
            </div>
          )}
          {blobUrl && !renderError && (
            <iframe
              src={blobUrl + '#toolbar=0&navpanes=0&scrollbar=1'}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="PDF Preview"
            />
          )}
          {!blobUrl && !rendering && !renderError && (
            <div style={cs.placeholder}>
              <div style={{ fontSize: 36 }}>📄</div>
              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 8 }}>
                {de ? 'Vorschau wird geladen...' : 'Loading preview...'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const cs = {
  panelTitle:  { fontSize: 12, fontWeight: 700, color: '#fff', margin: 0 },
  hint:        { fontSize: 10.5, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5, margin: 0 },
  sliderBlock: { background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '6px 8px' },
  sliderLabel: { fontSize: 10.5, color: 'rgba(255,255,255,0.65)', marginBottom: 4 },
  sliderRow:   { display: 'flex', alignItems: 'center', gap: 6 },
  slider:      { flex: 1, accentColor: '#1E5EFF', cursor: 'pointer' },
  sliderVal:   { fontSize: 10.5, fontFamily: 'monospace', color: '#4A9EFF', minWidth: 30, textAlign: 'right' },
  resetBtn:    { marginTop: 3, background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: 10, cursor: 'pointer', padding: 0 },
  resetAllBtn: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.55)', borderRadius: 8, padding: '7px', fontSize: 11, cursor: 'pointer', width: '100%' },
  overlay:     { position: 'absolute', inset: 0, background: 'rgba(20,20,30,0.8)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10 },
  spinner:     { width: 28, height: 28, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#4A9EFF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  errorBox:    { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#FCA5A5', fontSize: 12, gap: 10 },
  retryBtn:    { background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#F87171', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 11 },
  placeholder: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
};

if (typeof document !== 'undefined' && !document.getElementById('preview-spin')) {
  const st = document.createElement('style');
  st.id = 'preview-spin';
  st.textContent = '@keyframes spin{to{transform:rotate(360deg)}}';
  document.head.appendChild(st);
}
